"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronRight, Calendar, Eye } from "lucide-react";
function MainNewsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="space-y-6">
        <div className="rounded-lg overflow-hidden">
          <Skeleton className="h-80 w-full" />
        </div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <div key={i} className="flex gap-4">
              <Skeleton className="h-24 w-32 flex-shrink-0 rounded-md" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </div>)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="text-center text-muted-foreground py-8">
        Gagal memuat berita. Silakan coba lagi.
      </div>;
  }
  if (!posts.length) {
    return <p className="text-muted-foreground">Tidak ada berita tersedia.</p>;
  }
  const featuredPost = posts[0];
  const otherPosts = posts.slice(1);
  return <div className="space-y-8">
      {}
      {featuredPost && <Link to={featuredPost.link || "#"} className="block">
          <article className="rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
            {featuredPost.featuredImage && <div className="relative w-full h-80 bg-muted overflow-hidden">
                <img src={featuredPost.featuredImage} alt={featuredPost.title} className="w-full h-full object-cover" loading="lazy" />
              </div>}
            <div className="bg-background p-6">
              <h2 className="text-2xl font-bold text-foreground mb-3 hover:text-primary">
                {featuredPost.title}
              </h2>
              {featuredPost.excerpt && <p className="text-muted-foreground line-clamp-3 mb-4">
                  {featuredPost.excerpt}
                </p>}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                {featuredPost.date && <span className="flex items-center gap-1">
                    <SafeHydrate><Calendar className="w-4 h-4" /></SafeHydrate>
                    {new Date(featuredPost.date).toLocaleDateString("id-ID")}
                  </span>}
              </div>
            </div>
          </article>
        </Link>}

      {}
      <div className="space-y-4">
        {otherPosts.map((post, i) => <Link key={post.id} to={post.link || "#"} className="block" data-index={i + 1}>
            <article className="flex gap-4 hover:bg-accent p-4 rounded-lg transition-colors">
              {post.featuredImage && <div className="w-32 h-24 flex-shrink-0 rounded-md overflow-hidden bg-muted">
                  <img src={post.featuredImage} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
                </div>}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground line-clamp-2 hover:text-primary">
                  {post.title}
                </h3>
                {post.excerpt && <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                    {post.excerpt}
                  </p>}
                <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3">
                  {post.date && <span>
                      {new Date(post.date).toLocaleDateString("id-ID")}
                    </span>}
                </div>
              </div>
            </article>
          </Link>)}
      </div>
    </div>;
}
function SidebarAgendaConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="space-y-4">
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
      </div>;
  }
  if (error || !posts.length) {
    return null;
  }
  return <div className="space-y-4">
      {posts.slice(0, 3).map((post, i) => <Link key={post.id} to={post.link || "#"} className="block" data-index={i}>
          <Card className="hover:shadow-md transition-shadow cursor-pointer border">
            <CardContent className="p-4">
              <Badge variant="outline" className="mb-2 text-xs">
                {post.customFields?.kategori || "Agenda"}
              </Badge>
              <h4 className="font-semibold text-sm text-foreground line-clamp-2 hover:text-primary">
                {post.title}
              </h4>
              {post.customFields?.tanggal && <p className="text-xs text-muted-foreground mt-2">
                  {new Date(post.customFields.tanggal).toLocaleDateString("id-ID")}
                </p>}
            </CardContent>
          </Card>
        </Link>)}
    </div>;
}
function SidebarPressConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="space-y-4">
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
      </div>;
  }
  if (error || !posts.length) {
    return null;
  }
  return <div className="space-y-4">
      {posts.slice(0, 3).map((post, i) => <Link key={post.id} to={post.link || "#"} className="block" data-index={i}>
          <Card className="hover:shadow-md transition-shadow cursor-pointer border">
            <CardContent className="p-4">
              <Badge variant="outline" className="mb-2 text-xs">
                Press Release
              </Badge>
              <h4 className="font-semibold text-sm text-foreground line-clamp-2 hover:text-primary">
                {post.title}
              </h4>
              {post.customFields?.tanggal && <p className="text-xs text-muted-foreground mt-2">
                  {new Date(post.customFields.tanggal).toLocaleDateString("id-ID")}
                </p>}
            </CardContent>
          </Card>
        </Link>)}
    </div>;
}
export default function ZonaPPNewsGrid() {
  const newsQueryParams = {
    post_type: "zona_pp_warta",
    per_page: 10,
    order: "desc",
    orderby: "date"
  };
  const newsQuery = new WP_Query(newsQueryParams);
  const agendaQueryParams = {
    post_type: "zona_pp_warta",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const agendaQuery = new WP_Query(agendaQueryParams);
  const pressQueryParams = {
    post_type: "zona_pp_warta",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const pressQuery = new WP_Query(pressQueryParams);
  return <section id="zonappnewsgrid" className="bg-background py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {}
          <div className="lg:col-span-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-foreground mb-2 font-default">
                Warta & Agenda
              </h2>
              <p className="text-muted-foreground">
                Informasi berita, agenda, saiaran pera dan dokumentasi kegiatan Pemuda Pancasila dari tingkat nasional hingga tingkat basis.
              </p>
            </div>
            <PostsProvider wp_query={{
            post_type: "zona_pp_warta",
            per_page: 10,
            order: "desc",
            orderby: "date"
          }}>
              <MainNewsConsumer />
            </PostsProvider>
          </div>

          {}
          <div className="lg:col-span-4 space-y-8">
            {}
            <Card className="border">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-default">
                  Agenda Terkini
                </CardTitle>
              </CardHeader>
              <Separator />
              <CardContent className="pt-6">
                <PostsProvider wp_query={{
                post_type: "zona_pp_warta",
                per_page: 3,
                order: "desc",
                orderby: "date"
              }}>
                  <SidebarAgendaConsumer />
                </PostsProvider>
              </CardContent>
            </Card>

            {}
            <Card className="border">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-default">
                  Siaran Pers
                </CardTitle>
              </CardHeader>
              <Separator />
              <CardContent className="pt-6">
                <PostsProvider wp_query={{
                post_type: "zona_pp_warta",
                per_page: 3,
                order: "desc",
                orderby: "date"
              }}>
                  <SidebarPressConsumer />
                </PostsProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}